import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Shield, LogIn, LogOut, ShoppingCart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useUiTexts, useNav } from "@/lib/uiTexts";
import DiscordIcon from "@/components/DiscordIcon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const googleLogin = () => {
  window.location.href = `/api/auth/google/login`;
};

// Revient sur la page en cours après la connexion (le drafter, par exemple)
export const discordLogin = () => {
  window.location.href = `/api/auth/discord/login?next=${encodeURIComponent(window.location.pathname + window.location.hash)}`;
};

const LOGIN_OPTIONS = [
  { key: "google", label: "Google", onClick: googleLogin, Icon: LogIn },
  { key: "discord", label: "Discord", onClick: discordLogin, Icon: DiscordIcon },
];

function CartButton({ mobile = false, onAction }) {
  const { count, openCart } = useCart();
  if (mobile) {
    return (
      <button
        onClick={() => { openCart(); onAction?.(); }}
        data-testid="nav-cart-button-mobile"
        aria-label="Ouvrir le panier"
        className="relative flex items-center justify-between rounded-xl px-4 py-3 font-heading text-lg font-bold uppercase tracking-wide text-slate-300 transition-colors hover:bg-white/5 hover:text-orange-400"
      >
        <span className="flex items-center gap-2"><ShoppingCart className="h-5 w-5 text-orange-500" /> Panier</span>
        {count > 0 && (
          <span data-testid="nav-cart-count-mobile"
            className="flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 font-mono2 text-[11px] font-black text-[#060B18]">
            {count}
          </span>
        )}
      </button>
    );
  }
  return (
    <button
      onClick={() => { openCart(); onAction?.(); }}
      data-testid="nav-cart-button"
      aria-label="Ouvrir le panier"
      className="relative flex items-center gap-2 rounded-full border border-orange-500/30 bg-[#0E172F]/80 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-200 transition-all hover:border-orange-500 hover:bg-orange-500/10 hover:text-white hover:shadow-[0_0_15px_rgba(255,94,0,0.3)]"
    >
      <ShoppingCart className="h-4 w-4 text-orange-500" /> Panier
      {count > 0 && (
        <span data-testid="nav-cart-count"
          className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 font-mono2 text-[11px] font-black text-[#060B18] shadow-[0_0_10px_rgba(255,94,0,0.8)]">
          {count}
        </span>
      )}
    </button>
  );
}

function UserChip({ mobile = false, onAction }) {
  const { user, logout } = useAuth();
  if (!user && mobile) {
    return LOGIN_OPTIONS.map(({ key, label, onClick, Icon }) => (
      <button key={key} onClick={onClick} data-testid={`nav-login-${key}-mobile`}
        className="flex w-full items-center gap-2 rounded-xl px-4 py-3 font-heading text-lg font-bold uppercase tracking-wide text-slate-300 transition-colors hover:bg-white/5 hover:text-orange-400">
        <Icon className="h-5 w-5 text-orange-500" /> Connexion {label}
      </button>
    ));
  }
  if (!user) {
    return (
      <Popover>
        <PopoverTrigger data-testid="nav-login"
          className="flex items-center gap-2 rounded-full border border-slate-700 bg-[#0E172F]/80 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-200 transition-all hover:border-orange-500 hover:bg-orange-500/10 hover:text-white">
          <LogIn className="h-4 w-4 text-orange-500" /> Connexion
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto min-w-[14rem] whitespace-nowrap border-orange-500/20 bg-[#0A1024] p-2">
          {LOGIN_OPTIONS.map(({ key, label, onClick, Icon }) => (
            <button key={key} onClick={onClick} data-testid={`nav-login-${key}`}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-bold text-slate-200 transition-colors hover:bg-orange-500/10 hover:text-white">
              <Icon className="h-4 w-4 text-orange-500" /> Continuer avec {label}
            </button>
          ))}
        </PopoverContent>
      </Popover>
    );
  }
  if (mobile) {
    return (
      <div className="grid gap-1">
        <div className="flex items-center gap-3 px-4 py-2">
          {user.picture ? (
            <img src={user.picture} alt="" className="h-8 w-8 rounded-full border border-orange-500/60 object-cover" referrerPolicy="no-referrer" />
          ) : (
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-500 font-heading text-sm font-black text-[#060B18]">
              {(user.name || user.email || "?")[0].toUpperCase()}
            </span>
          )}
          <span data-testid="nav-user-name-mobile" className="max-w-[160px] truncate text-sm font-bold text-slate-200">
            {user.name || user.email}
          </span>
        </div>
        {user.role !== "admin" && (
          <Link to="/mes-commandes" data-testid="nav-my-orders-mobile" onClick={onAction}
            className="rounded-xl px-4 py-3 font-heading text-lg font-bold uppercase tracking-wide text-orange-400 transition-colors hover:bg-orange-500/10">
            Mes commandes
          </Link>
        )}
        <button onClick={() => { logout(); onAction?.(); }} data-testid="nav-logout-mobile"
          className="flex items-center gap-2 rounded-xl px-4 py-3 font-heading text-lg font-bold uppercase tracking-wide text-red-400 transition-colors hover:bg-red-500/10">
          <LogOut className="h-5 w-5" /> Déconnexion
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 rounded-full border border-slate-800 bg-[#0E172F]/80 py-1.5 pl-1.5 pr-3 backdrop-blur-md">
      {user.picture ? (
        <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-orange-500/60 object-cover shadow-[0_0_8px_rgba(255,94,0,0.4)]" referrerPolicy="no-referrer" />
      ) : (
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-orange-500 font-heading text-xs font-black text-[#060B18]">
          {(user.name || user.email || "?")[0].toUpperCase()}
        </span>
      )}
      <span data-testid="nav-user-name" className="max-w-[110px] truncate text-xs font-bold uppercase tracking-wide text-slate-200">
        {user.name || user.email}
      </span>
      {user.role !== "admin" && (
        <Link to="/mes-commandes" data-testid="nav-my-orders"
          className="text-xs font-bold uppercase tracking-wider text-orange-500 transition-colors hover:text-orange-300 hover:underline">
          Mes commandes
        </Link>
      )}
      <button onClick={() => { logout(); onAction?.(); }} data-testid="nav-logout"
        aria-label="Déconnexion" className="p-1 text-slate-400 transition-colors hover:text-red-400">
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

function AdminBadge({ mobile = false, onAction }) {
  return (
    <Link
      to="/admin"
      data-testid={mobile ? "nav-link-admin-mobile" : "nav-link-admin"}
      onClick={onAction}
      className={mobile
        ? "flex items-center gap-2 rounded-xl border border-orange-500/40 bg-orange-500/10 px-4 py-3 font-heading text-lg font-black uppercase text-orange-400"
        : "flex items-center gap-2 rounded-full border border-orange-500/50 bg-gradient-to-r from-orange-500/20 to-orange-600/10 px-4 py-2 text-xs font-black uppercase tracking-wider text-orange-500 shadow-[0_0_12px_rgba(255,94,0,0.2)] transition-all hover:scale-105 hover:bg-orange-500/20 hover:shadow-[0_0_20px_rgba(255,94,0,0.4)]"}
    >
      <Shield className="h-4 w-4" /> Admin
    </Link>
  );
}

export default function Navbar() {
  const { count, openCart } = useCart();
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const texts = useUiTexts();
  const nav = useNav();
  const NAV_ITEMS = {
    home: { to: "/", label: texts.nav_home, testid: "nav-link-home" },
    shop: { to: "/boutique", label: texts.nav_shop, testid: "nav-link-shop" },
    rosters: { to: "/rosters", label: texts.nav_rosters, testid: "nav-link-rosters" },
    drafter: { to: "/drafter", label: texts.nav_drafter, testid: "nav-link-drafter" },
    results: { to: "/resultats", label: texts.nav_results, testid: "nav-link-results" },
    news: { to: "/actualites", label: texts.nav_news, testid: "nav-link-news" },
    partners: { to: "/partenaires", label: texts.nav_partners, testid: "nav-link-partners" },
    contact: { to: "/contact", label: texts.nav_contact, testid: "nav-link-contact" },
  };
  const LINKS = nav.order
    .map((k) => {
      if (NAV_ITEMS[k]) return NAV_ITEMS[k];
      const custom = (nav.custom || []).find((c) => c.id === k);
      return custom ? { to: custom.url, label: custom.label, testid: `nav-link-custom-${custom.id}`, external: custom.url.startsWith("http") } : null;
    })
    .filter(Boolean);

  const linkCls = (isActive) =>
    `relative whitespace-nowrap rounded-full px-2.5 py-2 2xl:px-3 font-heading text-sm font-bold uppercase tracking-wider transition-all duration-200 ${
      isActive ? "font-extrabold text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]" : "text-slate-400 hover:bg-white/5 hover:text-slate-100"
    }`;

  const mobileLinkCls = (isActive) =>
    isActive
      ? "flex items-center justify-between rounded-xl border border-orange-500/40 bg-orange-500/10 px-4 py-3 font-heading text-lg font-black uppercase text-white shadow-[0_0_12px_rgba(255,94,0,0.2)]"
      : "rounded-xl px-4 py-3 font-heading text-lg font-bold uppercase tracking-wide text-slate-300 transition-colors hover:bg-white/5 hover:text-orange-400";

  return (
    <header className="sticky top-0 z-50 h-20 border-b border-orange-500/20 bg-[#060B18]/85 shadow-[0_4px_30px_rgba(0,0,0,0.5)] backdrop-blur-xl">
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between px-4 sm:px-8">
        {/* Marque */}
        <Link to="/" data-testid="nav-brand-logo" className="group flex shrink-0 items-center gap-3">
          <img src="/logo.png" alt="Emisphere Esport"
            className="h-10 w-10 object-contain drop-shadow-[0_0_12px_rgba(255,94,0,0.4)] transition-transform duration-300 group-hover:rotate-[-2deg] group-hover:scale-105" />
          <span className="flex items-center gap-2 whitespace-nowrap font-heading text-xl font-black uppercase tracking-tight text-white">
            Emisphere <span className="text-orange-500 drop-shadow-[0_0_8px_rgba(255,94,0,0.5)]">Esport</span>
            <span className="h-2 w-2 animate-pulse rounded-full bg-orange-500" />
          </span>
        </Link>

        {/* Liens — pilule centrale */}
        <nav className="hidden items-center gap-0.5 rounded-full border border-orange-500/15 bg-[#0A1024]/60 p-1.5 backdrop-blur-md xl:flex">
          {LINKS.map((l) => (
            l.external ? (
              <a key={l.to} href={l.to} target="_blank" rel="noreferrer" data-testid={l.testid} className={linkCls(false)}>
                {l.label}
              </a>
            ) : (
              <NavLink key={l.to} to={l.to} data-testid={l.testid} className={({ isActive }) => linkCls(isActive)}>
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <motion.span
                        layoutId="activeNavPill"
                        transition={{ type: "spring", stiffness: 350, damping: 30 }}
                        className="absolute inset-0 -z-10 rounded-full border border-orange-500/50 bg-gradient-to-r from-orange-500/20 to-orange-600/10 shadow-[0_0_12px_rgba(255,94,0,0.25)]"
                      />
                    )}
                    {l.label}
                  </>
                )}
              </NavLink>
            )
          ))}
        </nav>

        {/* Actions */}
        <div className="hidden items-center gap-2.5 xl:flex">
          <CartButton />
          <UserChip />
          {user?.role === "admin" && <AdminBadge />}
        </div>

        {/* Mobile */}
        <div className="flex items-center gap-2 xl:hidden">
          <button data-testid="nav-header-cart-button" onClick={openCart}
            aria-label={count > 0 ? `Ouvrir le panier, ${count} article${count > 1 ? "s" : ""}` : "Ouvrir le panier"}
            className="relative flex h-11 w-11 items-center justify-center rounded-lg border border-orange-500/30 text-slate-200 transition-colors hover:border-orange-500 hover:bg-orange-500/10">
            <ShoppingCart className="h-5 w-5 text-orange-500" />
            {count > 0 && (
              <span data-testid="nav-header-cart-count"
                className="absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-orange-500 px-1 font-mono2 text-[11px] font-black text-[#060B18]">
                {count}
              </span>
            )}
          </button>
          <button data-testid="nav-mobile-menu-button"
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-orange-500/30 text-slate-200 transition-colors hover:border-orange-500 hover:bg-orange-500/10"
            onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="fixed inset-x-0 top-20 border-b border-orange-500/20 bg-[#060B18] p-6 shadow-2xl xl:hidden"
          >
            <div className="flex flex-col gap-2">
              {LINKS.map((l) => (
                l.external ? (
                  <a key={l.to} href={l.to} target="_blank" rel="noreferrer" data-testid={`${l.testid}-mobile`}
                    onClick={() => setOpen(false)} className={mobileLinkCls(false)}>
                    {l.label}
                  </a>
                ) : (
                  <NavLink key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)}
                    className={({ isActive }) => mobileLinkCls(isActive)}>
                    {l.label}
                  </NavLink>
                )
              ))}
              <div className="mt-2 border-t border-orange-500/10 pt-3">
                <CartButton mobile onAction={() => setOpen(false)} />
                <UserChip mobile onAction={() => setOpen(false)} />
                {user?.role === "admin" && <AdminBadge mobile onAction={() => setOpen(false)} />}
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
